import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@apollo/client/react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScioMark } from "@/components/ui/scio-mark";
import { PublicHeader } from "@/components/layout/public-header";
import { REQUEST_PASSWORD_RESET } from "@/graphql/mutations/account";

/**
 * „Pamiršau slaptažodį."
 *
 * The acknowledgement is deliberately hedged — „jei tokia paskyra yra" — because
 * the server answers success for every address it is given, whether or not one
 * exists here. A definite „nuorodą išsiuntėme" would be a lie in one branch, and
 * a definite „tokio adreso nėra" would turn this form into a way of asking which
 * addresses have accounts.
 *
 * A Google account gets nothing, and that is right: it has no password to reset
 * and signing in with Google always works.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const [requestReset, { loading }] = useMutation(REQUEST_PASSWORD_RESET, {
    // Both branches say the same thing, for the same reason the server does:
    // this screen must not report anything that varies with the address.
    onCompleted: () => setSent(true),
    onError: () => setSent(true),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    requestReset({ variables: { email: email.trim() } });
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader action="login" />

      <div className="flex items-center justify-center px-4 pb-16 pt-4 sm:pt-10">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <ScioMark size={44} className="mx-auto mb-2 text-primary" title="Scio" />
            <CardTitle className="text-center text-2xl font-bold tracking-tight">
              Pamiršote slaptažodį?
            </CardTitle>
            <CardDescription className="text-center">
              Įrašykite el. paštą ir atsiųsime nuorodą naujam slaptažodžiui.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {sent ? (
              <>
                <Alert>
                  <AlertDescription>
                    Jei ši paskyra prisijungia su slaptažodžiu, nuorodą išsiuntėme.
                    Patikrinkite el. paštą, taip pat šlamšto (spam) dėžutę.
                  </AlertDescription>
                </Alert>

                {/* Named here because it is the commonest reason nothing arrives,
                    and somebody staring at an empty inbox has no way to guess it. */}
                <p className="text-sm text-muted-foreground">
                  Jei registravotės su Google, slaptažodžio nėra — prisijunkite su
                  Google mygtuku.
                </p>

                <Button asChild className="w-full">
                  <Link to="/auth/login">Grįžti į prisijungimą</Link>
                </Button>
              </>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">El. paštas</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="vardas@pastas.lt"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoFocus
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading || !email.includes("@")}>
                  {loading ? "Siunčiama..." : "Atsiųsti nuorodą"}
                </Button>

                <p className="text-center text-sm">
                  <Link
                    to="/auth/login"
                    className="text-muted-foreground underline underline-offset-4 hover:text-foreground"
                  >
                    Grįžti į prisijungimą
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
